import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { blogPosts, formatBlogDate, getAllCategories } from "./blog-content";
import { SEOHead } from "./SEOHead";
import { SiteNavbar } from "@/components/SiteNavbar";
import { SiteLink } from "@/components/SiteLink";
import { LayoutGrid, List } from "lucide-react";
import { useLocation } from "wouter";

// Convert category name to URL slug
function categoryToSlug(category: string): string {
  return category.toLowerCase().replace(/\s+/g, "-");
}

// Find category by slug (case-insensitive match)
function findCategoryBySlug(slug: string, categories: string[]): string | null {
  return (
    categories.find((cat) => categoryToSlug(cat) === slug.toLowerCase()) || null
  );
}

export function BlogListPage() {
  const [viewMode, setViewMode] = useState<"gallery" | "list">("gallery");
  const [location] = useLocation();

  // Scroll to top on mount and when location changes (for client-side navigation)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location]);

  // Get all unique categories
  const allCategories = getAllCategories();

  // Get selected category from URL path using Wouter's location
  const getCategoryFromPath = (): string | null => {
    const match = location.match(/^\/blog\/([^/]+)\/?$/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  const categorySlug = getCategoryFromPath();
  const selectedCategory = categorySlug
    ? findCategoryBySlug(categorySlug, allCategories)
    : null;

  // Filter posts by selected category
  const filteredPosts = blogPosts.filter((post) => {
    return (
      !selectedCategory ||
      post.category?.toLowerCase() === selectedCategory.toLowerCase()
    );
  });

  // SEO metadata based on category
  const pageTitle = selectedCategory ? `${selectedCategory} - Blog` : "Blog";
  const pageDescription = selectedCategory
    ? `${selectedCategory} posts from the Taias team.`
    : "Insights, tutorials, and updates from the Taias team. Learn how to build better MCP experiences.";
  const canonicalUrl = selectedCategory
    ? `https://taias.xyz/blog/${categoryToSlug(selectedCategory)}`
    : "https://taias.xyz/blog";

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950">
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
      />

      {/* Shared Navbar */}
      <SiteNavbar variant="standard" />

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
            {selectedCategory || "Blog"}
          </h1>
        </div>

        {/* Category filter + View toggle row */}
        <div className="flex items-center justify-between mb-10">
          {/* Category tabs */}
          <div className="flex items-center gap-6 text-sm">
            <SiteLink
              href="/blog"
              className={cn(
                "transition-colors",
                selectedCategory === null
                  ? "text-zinc-900 dark:text-white font-medium"
                  : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white",
              )}
            >
              All
            </SiteLink>
            {allCategories.map((category) => (
              <SiteLink
                key={category}
                href={`/blog/${categoryToSlug(category)}`}
                className={cn(
                  "transition-colors",
                  selectedCategory === category
                    ? "text-zinc-900 dark:text-white font-medium"
                    : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white",
                )}
              >
                {category}
              </SiteLink>
            ))}
          </div>

          {/* View toggle */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setViewMode("gallery")}
              className={cn(
                "p-2 rounded transition-colors",
                viewMode === "gallery"
                  ? "text-zinc-900 dark:text-white"
                  : "text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300",
              )}
              aria-label="Gallery view"
            >
              <LayoutGrid className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={cn(
                "p-2 rounded transition-colors",
                viewMode === "list"
                  ? "text-zinc-900 dark:text-white"
                  : "text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300",
              )}
              aria-label="List view"
            >
              <List className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Posts */}
        {filteredPosts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-zinc-500 dark:text-zinc-400">
              No posts found
              {selectedCategory ? ` in "${selectedCategory}"` : ""}.
            </p>
          </div>
        ) : viewMode === "gallery" ? (
          /* Gallery View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article key={post.slug} className="group">
                <SiteLink href={`/blog/${post.slug}`} className="block">
                  {/* Thumbnail image */}
                  <div className="aspect-[16/10] rounded-lg overflow-hidden bg-zinc-100 dark:bg-zinc-800 mb-4">
                    {post.ogImage ? (
                      <img
                        src={post.ogImage}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-zinc-200 to-zinc-300 dark:from-zinc-700 dark:to-zinc-800" />
                    )}
                  </div>

                  {/* Title */}
                  <h2 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2 group-hover:text-zinc-600 dark:group-hover:text-zinc-300 transition-colors">
                    {post.title}
                  </h2>

                  {/* Category + Date */}
                  <div className="flex items-center gap-3 text-sm text-zinc-500 dark:text-zinc-400">
                    {post.category && (
                      <span className="text-zinc-700 dark:text-zinc-300">
                        {post.category}
                      </span>
                    )}
                    <time dateTime={post.date}>
                      {formatBlogDate(post.date)}
                    </time>
                  </div>
                </SiteLink>
              </article>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {filteredPosts.map((post) => (
              <article key={post.slug} className="group py-6 first:pt-0">
                <SiteLink href={`/blog/${post.slug}`} className="flex gap-12">
                  {/* Left column: Category + Date */}
                  <div className="w-32 shrink-0">
                    {post.category && (
                      <div className="text-sm text-zinc-900 dark:text-zinc-100 mb-1">
                        {post.category}
                      </div>
                    )}
                    <time
                      dateTime={post.date}
                      className="text-sm text-zinc-500 dark:text-zinc-400"
                    >
                      {formatBlogDate(post.date)}
                    </time>
                  </div>

                  {/* Right column: Title + Description */}
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2 group-hover:text-zinc-600 dark:group-hover:text-zinc-300 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 line-clamp-2">
                      {post.description}
                    </p>
                  </div>
                </SiteLink>
              </article>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-200 dark:border-zinc-800 mt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between text-sm text-zinc-500 dark:text-zinc-500">
            <span>© {new Date().getFullYear()} Taias</span>
            <div className="flex items-center gap-6">
              <SiteLink
                href="/docs"
                className="hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
              >
                Docs
              </SiteLink>
              <a
                href="https://github.com/taias"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
              >
                GitHub
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default BlogListPage;
